import { Container } from 'src/components/common/Container'
import { CustomSwiper } from 'src/components/common/CustomSwiper'
import LazyImage from 'src/components/common/LazyImage'
import { Typography } from 'src/components/common/Typography'
import { PreviewPageProps } from 'src/pages/PreviewPage'
import {
  Noto_body2_r14,
  Noto_body3_m12,
  Noto_body3_r12,
  Noto_desc_m10,
  Noto_subheading_b18,
} from 'src/styles/fonts/NotoSansKrFonts'
import { Roboto_num1_b20, Roboto_num3_r14, Roboto_num4_b12 } from 'src/styles/fonts/RobotoFonts'
import { ellip, mediaMaxWidth } from 'src/styles/mixin'
import styled, { css, keyframes } from 'styled-components'
import thumbnail4 from 'src/assets/images/preview/preview_hotdeal_thumbnail4.png'
import lock from 'src/assets/images/preview/preview_lock.png'
import { useEffect, useRef, useState } from 'react'
import useResizeEventListener from 'src/hooks/useResizeEventListener.hook'
import { SwiperRef } from 'swiper/react'
import type { Swiper as SwiperClass } from 'swiper/types'
import { click_tracking_dataLayer } from 'src/utils/gtmDataLayer'

const PreviewHotDeal = (props: PreviewPageProps) => {
  const { hotDealCouponList, handleExternalOpenUrl } = props
  const containerRef = useRef<HTMLDivElement>(null)
  const [left, setLeft] = useState<string>('0px')
  const swiperRef = useRef<SwiperRef>(null)

  const calculateLeftPosition = () =>
    setLeft(`${(containerRef?.current?.clientWidth! - 420) / 2}px`)

  const handleClickItem = () => {
    click_tracking_dataLayer('top_rolling')
    handleExternalOpenUrl('main')
  }

  useEffect(() => {
    const rafId = requestAnimationFrame(calculateLeftPosition)
    return () => {
      window.cancelAnimationFrame(rafId)
    }
  }, [])

  useResizeEventListener(calculateLeftPosition)

  const BASE_SPEED = 8000
  const MIN_SPEED = 180

  return (
    <S.PreviewHotDealContainer ref={containerRef}>
      <S.Title.Container $option="flex_row" $column_gap="8px" $left={left}>
        <S.Title.Point $fontStyle={Roboto_num4_b12} $color="var(--white)">
          HOT
        </S.Title.Point>
        <S.Title.Text $fontStyle={Noto_subheading_b18} $color="var(--black-90)">
          최저가 도전!
        </S.Title.Text>
      </S.Title.Container>
      <S.Description $fontStyle={Noto_body2_r14} $color="var(--black-50)" $left={left}>
        선착순 초특가 할인 쿠폰
      </S.Description>
      <CustomSwiper.Wrap
        ref={swiperRef}
        type="default"
        $wrapstyle={S.CouponCardSwiperStyle(left)}
        gap={10}
        swiperOption={{
          speed: 8000,
          initialSlide: 0,
          resistanceRatio: 0,
          autoplay: {
            delay: 0,
            disableOnInteraction: false,
            stopOnLastSlide: true,
            waitForTransition: false,
          },
          threshold: 8,
          onSwiper: (swiper: SwiperClass) => {
            swiper.autoplay?.stop()
            swiper.slideTo(0, 0, false)
            window.setTimeout(() => swiper.autoplay?.start(), 1000)
          },
          onResize: (swiper: SwiperClass) => {
            swiper.update()
            swiper.slideTo(0, 0, false)
          },
          onTouchStart: (swiper: SwiperClass) => {
            swiper.autoplay?.stop()
          },
          onTouchEnd: (swiper: SwiperClass) => {
            const toIndex = swiper.activeIndex
            const fromIndex = swiper.previousIndex
            const fromSnap = swiper.snapGrid[fromIndex] ?? 0
            const toSnap = swiper.snapGrid[toIndex] ?? fromSnap
            const current = -swiper.getTranslate()
            const fullDistance = Math.max(Math.abs(toSnap - fromSnap), 1)
            const remainDistance = Math.max(Math.abs(toSnap - current), 0)
            const proportionalSpeed = Math.max(
              MIN_SPEED,
              Math.round(BASE_SPEED * (remainDistance / fullDistance)),
            )
            swiper.slideTo(toIndex, proportionalSpeed, false)
            window.setTimeout(() => {
              swiper.autoplay?.start()
            }, 1000)
          },
        }}
      >
        {hotDealCouponList?.map((ele, index: number) => {
          const { CouponName, CouponImage, DiscountRate, SalePrice, BrandName, RegularPrice } = ele

          return (
            <CustomSwiper.Item key={index} onClick={handleClickItem}>
              <S.CouponCard.Container $option="flex_column" $row_gap="6px">
                <S.CouponCard.ImageContainer>
                  <S.CouponCard.Chip $bgColor="#A6A6A6">판매완료</S.CouponCard.Chip>
                  <LazyImage src={CouponImage} alt={'쿠폰이미지'} style={S.CouponCard.Image} />
                </S.CouponCard.ImageContainer>
                <S.CouponCard.TextContainer $option="flex_column">
                  <S.CouponCard.DiscountContainer $option="flex_row" $column_gap="4px">
                    <S.CouponCard.DiscountRate $fontStyle={Roboto_num1_b20} $color="var(--red-50)">
                      {DiscountRate}%
                    </S.CouponCard.DiscountRate>
                    <S.CouponCard.RegularPrice
                      $fontStyle={Roboto_num3_r14}
                      $color="var(--black-30)"
                    >
                      {RegularPrice?.toLocaleString()}원
                    </S.CouponCard.RegularPrice>
                  </S.CouponCard.DiscountContainer>
                  <S.CouponCard.SalePrice $fontStyle={Roboto_num1_b20} $color="var(--black-90)">
                    {SalePrice?.toLocaleString()}원
                  </S.CouponCard.SalePrice>
                  <S.CouponCard.CouponBrand $fontStyle={Noto_body3_m12} $color="var(--black-90)">
                    {BrandName}
                  </S.CouponCard.CouponBrand>
                  <S.CouponCard.CouponName $fontStyle={Noto_body3_r12} $color="var(--black-90)">
                    {CouponName}
                  </S.CouponCard.CouponName>
                </S.CouponCard.TextContainer>
              </S.CouponCard.Container>
            </CustomSwiper.Item>
          )
        })}
        <CustomSwiper.Item key={'hidden-card'} onClick={handleClickItem}>
          <S.CouponCard.Container $option="flex_column" $row_gap="6px">
            <S.CouponCard.ImageContainer>
              <S.CouponCard.Chip $bgColor="var(--red-50)" $right="8px">
                선착순 판매중
              </S.CouponCard.Chip>
              <S.HiddenCard.ImageBorder>
                <LazyImage src={thumbnail4} alt={'쿠폰이미지'} style={S.HiddenCard.Image} />
              </S.HiddenCard.ImageBorder>
            </S.CouponCard.ImageContainer>
            <S.CouponCard.TextContainer $option="flex_column">
              <S.CouponCard.DiscountContainer $option="flex_row" $column_gap="4px">
                <S.CouponCard.DiscountRate $fontStyle={Roboto_num1_b20} $color="var(--red-50)">
                  ??
                </S.CouponCard.DiscountRate>
                <Typography $fontStyle={Roboto_num3_r14} $color="var(--black-30)">
                  ??????
                </Typography>
              </S.CouponCard.DiscountContainer>
              <S.CouponCard.SalePrice $fontStyle={Roboto_num1_b20} $color="var(--black-90)">
                <S.HiddenCard.Container $option="flex_row" $column_gap="5px">
                  <LazyImage src={lock} alt={'비밀가격'} style={S.HiddenCard.LockImage} />
                  비밀가격
                </S.HiddenCard.Container>
              </S.CouponCard.SalePrice>
            </S.CouponCard.TextContainer>
          </S.CouponCard.Container>
        </CustomSwiper.Item>
      </CustomSwiper.Wrap>
    </S.PreviewHotDealContainer>
  )
}

export default PreviewHotDeal

const rotateBorderGradient = keyframes`
  from {
    --angle: 0deg;
  }
  to {
    --angle: 360deg;
  }
`

const S = {
  PreviewHotDealContainer: styled(Container)``,

  Title: {
    Container: styled(Container)<{ $left?: string }>`
      align-items: center;
      height: 32px;
      padding: 0 ${({ $left }) => $left};

      ${mediaMaxWidth('460')} {
        padding: 0 20px;
      }
    `,
    Text: styled(Typography)`
      line-height: unset;
    `,
    Point: styled(Typography)`
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 4px;
      background: linear-gradient(180deg, #ff4545 0%, #fe9d02 100%);
      padding: 4px 6px;
      width: fit-content;
      height: 20px;
    `,
  },

  Description: styled(Typography)<{ $left?: string }>`
    display: flex;
    padding: 0 ${({ $left }) => $left};

    ${mediaMaxWidth('460')} {
      padding: 0 20px;
    }
  `,

  SearchPopularCoupon: {
    Container: css`
      margin: 30px auto 50px auto;
    `,
    PrimaryText: styled(Typography)`
      padding: 0 20px;
    `,
  },
  CouponCard: {
    Container: styled(Container)`
      cursor: pointer;
      width: 205px;

      ${mediaMaxWidth('460')} {
        width: 156px;
      }
    `,
    ImageContainer: styled(Container)`
      position: relative;
    `,
    Chip: styled.span<{ $bgColor: string; $right?: string }>`
      position: absolute;
      top: 8px;
      right: ${({ $right }) => $right || '6px'};
      padding: 2px 6px;
      border-radius: 4px;
      background-color: ${({ $bgColor }) => $bgColor};
      z-index: 1;
      color: var(--white);
      transform: translateZ(0);
      ${Noto_desc_m10};
    `,
    Image: css`
      border-radius: 12px;
      border: 1px solid rgba(0, 0, 0, 0.05);
      width: 205px;
      height: 205px;

      ${mediaMaxWidth('460')} {
        width: 156px;
        height: 156px;
      }
    `,
    TextContainer: styled(Container)``,
    DiscountContainer: styled(Container)`
      align-items: center;
    `,
    DiscountRate: styled(Typography)``,
    RegularPrice: styled(Typography)`
      text-decoration: line-through;
    `,
    SalePrice: styled(Typography)`
      margin-bottom: 4px;
    `,
    CouponBrand: styled(Typography)``,
    CouponName: styled(Typography)`
      ${ellip('line2')};
    `,
  },
  CouponCardSwiperStyle: (left: string) => css`
    display: flex;
    flex-direction: row;
    gap: 7px;
    margin-top: 20px;
    width: 100%;

    padding: 0 ${left};

    ${mediaMaxWidth('460')} {
      padding: 0 20px;
    }

    .swiper-wrapper {
      transition-timing-function: linear !important;
    }
  `,

  HiddenCard: {
    Container: styled(Container)`
      align-items: center;
    `,
    ImageBorder: styled(Container)`
      width: 205px;
      height: 205px;
      padding: 4px;
      border-radius: 12px;
      background: conic-gradient(
        from var(--angle) at 50% 50%,
        #ff4444 0deg,
        #ff4444 45deg,
        #cc0000 90deg,
        #ff4444 135deg,
        #ffbbbb 180deg,
        #ff4444 360deg
      );
      animation: ${rotateBorderGradient} 2.5s linear infinite;
      @property --angle {
        syntax: '<angle>';
        inherits: false;
        initial-value: 0deg;
      }

      ${mediaMaxWidth('460')} {
        width: 156px;
        height: 156px;
      }
    `,
    Image: css`
      width: 197px;
      height: 197px;
      border-radius: 9px;

      ${mediaMaxWidth('460')} {
        width: 148px;
        height: 148px;
      }
    `,
    LockImage: css`
      width: 21px;
      height: 21px;
    `,
  },
}
